/**
 * Sustainability Report generator page.
 *
 * Accessible at /sustainability-report — designers paste/select listing IDs
 * (or arrive pre-populated from their favorites/Revit) and download a branded
 * PDF summary suitable for Green Star / NABERS hand-off.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Leaf, Download, FileText } from 'lucide-react';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function SustainabilityReportPage() {
  const [listingIds, setListingIds] = useState('');
  const [projectName, setProjectName] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const prefill = sessionStorage.getItem('patinamart-prefill-ids');
    if (prefill) {
      setListingIds(prefill);
      sessionStorage.removeItem('patinamart-prefill-ids');
    }
  }, []);

  const parseIds = () =>
    listingIds
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handlePreview = async () => {
    const ids = parseIds();
    if (ids.length === 0) {
      toast.error('Paste at least one listing ID');
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/listings/carbon-totals`, { listing_ids: ids });
      setPreview(data);
      if (!data.items?.length) {
        toast.warning('None of those IDs matched a listing with carbon data');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async () => {
    const ids = parseIds();
    if (ids.length === 0) {
      toast.error('Paste at least one listing ID');
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/listings/sustainability-report`,
        { listing_ids: ids, project_name: projectName, requested_by: requestedBy },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (projectName || 'patinamart-sustainability').toLowerCase().replace(/\s+/g, '-');
      a.download = `${safe}-carbon-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-12 max-w-4xl" data-testid="sustainability-report-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2 inline-flex items-center gap-2">
          <Leaf className="w-3 h-3" /> Whole-Life Carbon
        </p>
        <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-3">
          Project Sustainability Report
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Drop the PatinaMart listing IDs from your project schedule below. We'll generate a one-page
          PDF summarising the embodied-carbon savings (kg CO<sub>2</sub>e avoided vs. buying new) using the
          Bath ICE v3.0 factors referenced by RICS Whole-Life Carbon Assessments — perfect for Green Star
          or NABERS submissions.
        </p>
      </div>

      <div className="rounded-xl border border-border/30 bg-card/30 p-6 md:p-8 space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Listing IDs <span className="text-foreground/60">(paste comma- or newline-separated)</span>
          </label>
          <Textarea
            value={listingIds}
            onChange={(e) => setListingIds(e.target.value)}
            rows={5}
            placeholder="lst_abc123def456&#10;lst_xyz789..."
            className="font-mono text-sm"
            data-testid="report-listing-ids"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Project name <span className="text-foreground/60">(optional)</span>
            </label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Heritage House Renovation"
              data-testid="report-project-name"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Prepared for <span className="text-foreground/60">(optional)</span>
            </label>
            <Input
              value={requestedBy}
              onChange={(e) => setRequestedBy(e.target.value)}
              placeholder="Architect / client name"
              data-testid="report-requested-by"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePreview}
            disabled={previewing}
            data-testid="report-preview-btn"
          >
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Preview totals
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="report-download-btn"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
        </div>
      </div>

      {preview && (
        <div className="mt-8 p-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5" data-testid="report-preview-panel">
          <p className="text-xs uppercase tracking-wider text-emerald-300/70 mb-2">Estimated savings</p>
          <p className="text-4xl md:text-5xl font-bold text-emerald-300 mb-1">
            {preview.total_tonnes_co2e >= 1
              ? `${preview.total_tonnes_co2e.toFixed(2)} t`
              : `${Math.round(preview.total_kg_co2e)} kg`}{' '}
            CO<sub>2</sub>e
          </p>
          <p className="text-sm text-emerald-200/80 mb-4">
            avoided across <strong>{preview.items.length}</strong> reclaimed items
          </p>
          {preview.items.length > 0 && (
            <ul className="text-xs text-foreground/70 space-y-1 max-h-60 overflow-y-auto">
              {preview.items.slice(0, 25).map((it) => (
                <li key={it.listing_id} className="flex justify-between gap-3">
                  <span className="truncate">{it.title}</span>
                  <span className="text-emerald-300 flex-shrink-0">{Math.round(it.kg_co2e)} kg</span>
                </li>
              ))}
              {preview.items.length > 25 && (
                <li className="text-muted-foreground italic">
                  …plus {preview.items.length - 25} more (all included in the PDF)
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
